import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ActivityLeaderboard({ activityType, activityId, activityTitle, onClose }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [activityId, activityType]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/leaderboard/activity/${activityType}/${activityId}`
      );
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching activity leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}º`;
  };

  const getActivityIcon = () => {
    if (activityType === 'quiz') return '📝';
    if (activityType === 'texthunt') return '📚';
    if (activityType === 'hangman') return '🎯';
    return '📊';
  };

  const getActivityTypeName = () => {
    if (activityType === 'quiz') return 'Quiz';
    if (activityType === 'texthunt') return 'TextHunt';
    if (activityType === 'hangman') return 'Ahorcado';
    return 'Actividad';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <span>{getActivityIcon()}</span>
                Clasificación - {getActivityTypeName()}
              </h2>
              <p className="text-purple-100 mt-1">{activityTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600">Cargando clasificación...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay resultados aún
              </h3>
              <p className="text-gray-500">
                Ningún estudiante ha completado esta actividad todavía
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry) => (
                <div
                  key={entry.student_id}
                  className={`
                    flex items-center justify-between p-4 rounded-lg border-2 transition hover:shadow-md
                    ${entry.rank === 1 ? 'bg-yellow-50 border-yellow-300' : ''}
                    ${entry.rank === 2 ? 'bg-gray-50 border-gray-300' : ''}
                    ${entry.rank === 3 ? 'bg-orange-50 border-orange-300' : ''}
                    ${entry.rank > 3 ? 'bg-white border-gray-200' : ''}
                  `}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Rank */}
                    <div className="text-3xl font-bold min-w-[60px] text-center">
                      {getMedalEmoji(entry.rank)}
                    </div>

                    {/* Student Info */}
                    <div className="flex items-center gap-3 flex-1">
                      {entry.student_picture && (
                        <img
                          src={entry.student_picture}
                          alt={entry.student_name}
                          className="w-12 h-12 rounded-full"
                        />
                      )}
                      <div>
                        <div className="text-lg font-bold text-gray-900">
                          {entry.student_name}
                        </div>
                        {entry.completed_at && (
                          <div className="text-xs text-gray-500">
                            Completado: {new Date(entry.completed_at).toLocaleDateString('es-ES')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Score */}
                    <div className="text-right">
                      <div className="text-2xl font-bold text-purple-600">
                        {entry.score}
                      </div>
                      <div className="text-xs text-gray-500">puntos</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Total de participantes: <span className="font-bold">{leaderboard.length}</span>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActivityLeaderboard;
